/* eslint-disable no-console */
import axios from 'axios';
import {jwtDecode} from 'jwt-decode';

import {CLIMATE_DB_BASE_URL, CLIMATE_DB_ENDPOINT_JSON} from '../../../config';
import CRUDMethods from '../../../constants/CRUDConstants';
import useCompanyStore from '../../../store/companyStore/companyDetailsStore';
import useUserStore from '../../../store/userStore/userStore';

export const AI_TOKEN_STORAGE_KEY = 'ai_token';

/**
 * Refresh slightly ahead of the real expiry so a session that is open across the
 * boundary does not start failing mid-use.
 */
const EXPIRY_BUFFER_SECONDS = 60 * 60 * 24;

interface GetTokenResponse {
  token?: string;
}

/**
 * De-dupes concurrent callers. Without this, two features asking for the token
 * during the same load would each issue a request.
 */
let inFlight: Promise<string | null> | null = null;

/**
 * A token is reusable unless it is malformed or (per its own `exp`) about to
 * expire. A token carrying no `exp` never expires, so it stays usable.
 */
const isTokenUsable = (token: string | null): token is string => {
  if (!token) {
    return false;
  }

  try {
    const {exp} = jwtDecode<{exp?: number}>(token);

    if (exp === undefined) {
      return true;
    }

    return exp - EXPIRY_BUFFER_SECONDS > Date.now() / 1000;
  } catch {
    return false;
  }
};

export const getStoredAIToken = (): string | null => {
  const token = localStorage.getItem(AI_TOKEN_STORAGE_KEY);

  return isTokenUsable(token) ? token : null;
};

const requestAIToken = async (): Promise<string | null> => {
  try {
    // No Authorization header: the platform authorises this route by request
    // origin. `Referer` is a forbidden header, so the browser sets it for us and
    // the calling origin must be allow-listed on the platform side.
    const response = await axios.request<GetTokenResponse>({
      method: CRUDMethods.GET,
      url: `${CLIMATE_DB_BASE_URL}${CLIMATE_DB_ENDPOINT_JSON.getToken}`
    });

    const token = response.data?.token;

    if (!token) {
      return null;
    }

    localStorage.setItem(AI_TOKEN_STORAGE_KEY, token);
    useUserStore.getState().setAIToken(token);

    return token;
  } catch (err) {
    // Swallowed on purpose: this runs inside the login flow, and Climate DB
    // being unreachable must not stop the user getting into the app.
    console.log('ai token err:', err);

    return null;
  }
};

/**
 * Peer universe is loaded once a token is in hand. A cached token never enters
 * /get-token, so this still runs on that path when the saved universe belongs
 * to a different company.
 */
const syncPeerUniverseAfterToken = (): void => {
  const companyId = useCompanyStore.getState().companyId?.trim() ?? '';

  if (!companyId) {
    return;
  }

  void import('./peerUniverseAPI').then(
    async ({readStoredPeerUniverse, syncPeerUniverseForCurrentCompany}) => {
      if (readStoredPeerUniverse(companyId)) {
        return;
      }

      await syncPeerUniverseForCurrentCompany();
    }
  );
};

/**
 * Returns a usable Climate DB token, fetching one only when there is no valid
 * cached token. The token lasts a year, so the common path performs no request.
 */
export const ensureAIToken = async (): Promise<string | null> => {
  const cached = getStoredAIToken();

  if (cached) {
    // Rehydrate the store on a fresh page load, where only localStorage survived.
    if (useUserStore.getState().aiToken !== cached) {
      useUserStore.getState().setAIToken(cached);
    }

    syncPeerUniverseAfterToken();

    return cached;
  }

  // A stale or malformed value would otherwise be read again on every call.
  localStorage.removeItem(AI_TOKEN_STORAGE_KEY);

  inFlight =
    inFlight ??
    requestAIToken().finally(() => {
      inFlight = null;
    });

  const token = await inFlight;

  if (token) {
    syncPeerUniverseAfterToken();
  }

  return token;
};
